// Phase 2Z.aa - Effective plan helper for UI gating.
//
// Returns a stable label the UI can compare against to decide whether
// to render premium controls, lock-state cards or upgrade nudges.
//
// Reads /billing/me, whose shape is:
//   {
//     plan_slug: "starter" | "growth" | "business" | "enterprise" | null,
//     active_employees: number,
//     max_employees: number | null,
//     access: { access: "full" | "restricted", reason: "trial" | "founding_customer" | ... },
//     ...
//   }
//
// Resolution order:
//   1. access.access === "restricted" -> "restricted"
//   2. trial in progress    -> "trial-command"   (full Command access)
//   3. founder in progress  -> "founder-command" (full Command-level access)
//   4. paid + plan_slug     -> "starter" | "growth" | "command" | "enterprise"
//   5. anything else        -> "unknown" (treat as Command-permissive)

export function effectivePlan(me) {
  if (!me) return "unknown";
  const accessBlock = me.access || {};
  if (accessBlock.access === "restricted") return "restricted";
  const reason = accessBlock.reason || "";
  if (reason === "trial") return "trial-command";
  if (reason === "founding_customer") return "founder-command";
  if (reason && reason.startsWith("override_")) return "override-command";
  const slug = me.plan_slug;
  if (slug === "starter") return "starter";
  if (slug === "growth") return "growth";
  if (slug === "business") return "command";
  if (slug === "enterprise") return "enterprise";
  return "unknown";
}

// True when the customer is currently entitled to Command-only features
// (AI Assistant, Autopilot). Trial / founder bypass.
export function hasCommandAccess(me) {
  const p = effectivePlan(me);
  return p === "trial-command" || p === "founder-command"
      || p === "override-command" || p === "command";
}

// True when the customer is currently entitled to Growth-tier features
// (Rota Style, Pattern Engine, Coverage Guard, RMI Mix, Performance
// Focus, CSV import). Anyone with Command access also passes.
export function hasGrowthAccess(me) {
  const p = effectivePlan(me);
  return hasCommandAccess(me) || p === "growth";
}

// True for paid Starter customers who must see locked-state cards on
// Growth-tier pages. Trial / founder / Growth / Command / Enterprise
// / restricted / unknown all return false.
export function isPaidStarter(me) {
  return effectivePlan(me) === "starter";
}
